<template>
  <div class="edit-page-wrapper">
    <form
      autocomplete="off"
      @submit.prevent="handleFormSubmit"
      v-if="!preloaderVisibility"
    >
      <div class="container">
        <div class="row">
          <div class="col-6">
            <h2>Name</h2>
            <label>
              <input
                type="text"
                placeholder="First Name"
                v-model="userInfo.firstName"
                :class="{ 'not-filled': highlightingData.firstName }"
              />
            </label>
            <label>
              <input
                type="text"
                placeholder="Last Name"
                v-model="userInfo.lastName"
                :class="{ 'not-filled': highlightingData.lastName }"
              />
            </label>
          </div>
          <div class="col-6">
            <h2>Authentication</h2>
            <label>
              <input
                type="email"
                placeholder="User Email"
                autocomplete="off"
                :class="{ 'not-filled': highlightingData.email }"
                v-model="userInfo.email"
              />
            </label>
            <label for="password">
              <input
                id="password"
                type="password"
                autocomplete="off"
                placeholder="New Password"
                minlength="6"
                v-model="userInfo.password"
              />
            </label>
            <label for="confirm-password">
              <input
                id="confirm-password"
                type="password"
                placeholder="Confirm New Password"
                minlength="6"
                v-model="validateFields.passwordConfirm"
              />
            </label>
          </div>
          <div class="col-6">
            <h2>Birth Details</h2>
            <label>
              <input
                :max="new Date().toISOString().split('T')[0]"
                type="date"
                v-model="dateTimeFields.date"
                required="required"
              />
            </label>
            <label>
              <input
                type="time"
                v-model="dateTimeFields.time"
                required="required"
              />
            </label>
            <label class="vue-select" :class="{ isRequired: highlightingData.countryOfBirth }">
              <vueSelect
                required="required"
                v-model="userInfo.birthCountry"
                :value="countrySearch"
                @search="getSelectedCountry"
                :options="countriesList"
                :multipe="false"
                :closeOnSelect="true"
                placeholder="Country of Birth"
              />
            </label>
            <label class="vue-select" :class="{ isRequired: highlightingData.cityOfBirth }">
              <vueSelect
                :disabled="!userInfo.birthCountry"
                required="required"
                v-model="userInfo.birthCity"
                label="cityFullName"
                @search="getSelectedCity"
                :options="citiesList"
                :multiple="false"
                :closeOnSelect="true"
                placeholder="City of Birth"
              />
            </label>
          </div>

          <div class="col-6">
            <h2>Language</h2>
            <label class="vue-select">
              <vueSelect
                v-model="userInfo.language"
                :options="languageOptions"
                :reduce="option => option.value"
                label="label"
                placeholder="Select Language"
                :clearable="false"
              />
            </label>
            <h2>Sign Up Date</h2>
            <label>
              <input type="text" :value="signUpDate" disabled />
            </label>
            <h2>Subscription</h2>
            <label>
              <input type="text" :value="subscription" disabled />
            </label>
            <div class="check-subscription-wrapper">
              <p>{{ `Last Checked: ${userInfo.lastSubscriptionCheck}` }}</p>
              <button
                type="button"
                class="check-subscription-btn"
                :class="{ checking: awaitResponse }"
                @click="updateUserSubcription"
              >
                <span v-if="awaitResponse"> Checking... </span>
                <span v-else>Check Subscription Status</span>
                <span class="sub-message" :class="{ active: checkComplete }"
                  >Check Complete</span
                >
              </button>
            </div>
            <div v-if="subscription !== 'Premium'">
              <h2>Overwrite Access</h2>
              <label class="vue-select">
                <vueSelect
                  class="subscription-select"
                  v-model="forceSubscription"
                  placeholder="Subscription"
                  :cleareable="false"
                  :options="['None', 'Force Premium']"
                  :multiple="false"
                  required="required"
                />
              </label>
              <label v-if="forceSubscription === 'Force Premium'">
                <input
                  :min="new Date().toISOString().split('T')[0]"
                  name="endDate"
                  type="date"
                  v-model="endOfAccess"
                  required="required"
                />
              </label>
            </div>
          </div>
        </div>
        <div class="row" v-if="errorMessage.length">
          <div class="col-6">
            <div class="errors-wrapper">
              <div v-for="(singleError, index) in errorMessage" :key="index">
                {{ singleError }}
              </div>
            </div>
          </div>
        </div>
        <div class="row">
          <div class="col-6 buttons-wrapper">
            <button @click.prevent="handlePopupOpen">Delete</button>
          </div>
          <div class="col-6 buttons-wrapper">
            <div>
              <p>{{ userInfo.lastUserSession ? `Last Session: ${userInfo.lastUserSession}` : '' }}</p>
            </div>
            <div>
              <button @click.prevent="handleRedirect">Cancel</button>
              <input type="submit" value="Save" />
            </div>
          </div>
        </div>
      </div>
    </form>
    <PreloaderComponent v-if="preloaderVisibility" />
    <ModalWindow
      v-if="modalView"
      :id="id"
      @deleteUser="handleDeleteUser"
      @closePopup="handlePopupClose"
    />
  </div>
</template>

<script>
import debounce from "debounce";
import vueSelect from "vue-select";
import "vue-select/dist/vue-select.css";

import SolFileAPIInstance from "@/api";
import ModalWindow from "@/components/common/ModalWindow";
import PreloaderComponent from "@/components/common/PreloaderComponent";
import { capitalize } from "../DiscoveryTable/DiscoveryTableSorting/DiscoveryTableSortingSingle.vue";

export default {
  name: "EditUserForm",
  data() {
    return {
      awaitResponse: false,
      checkComplete: false,
      id: this.$route.params.id,
      modalView: false,
      userInfo: {
        firstName: "",
        lastName: "",
        email: "",
        password: "",
        birthCity: null,
        birthCountry: null,
        language: "en",
      },
      validateFields: {
        passwordConfirm: "",
      },
      dateTimeFields: {
        date: "",
        time: "",
      },
      endOfAccess: null,
      signUpDate: "",
      subscription: "",
      forceSubscription: "None",
      errorMessage: [],

      countrySearch: "",
      countriesList: [],

      citySearch: "",
      citiesList: [],

      languageOptions: [
        { label: 'English', value: 'en' },
        { label: 'German', value: 'de' }
      ],

      preloaderVisibility: true,

      highlightingData: {
        firstName: false,
        lastName: false,
        email: false,
        passwordConfirm: false,
        password: false,
        cityOfBirth: false,
        countryOfBirth: false,
        timeOfBirth: false,
        dateOfBirth: false,
      },
    };
  },
  components: {
    vueSelect,
    ModalWindow,
    PreloaderComponent,
  },
  mounted() {
    this.getUser();
  },

  watch: {
    "userInfo.birthCountry"(value) {
      if (!value) {
        this.userInfo.birthCity = null;
      }
    },
  },

  methods: {
    async getUser() {
      const user = await SolFileAPIInstance.getUserById(this.id);
      if (user) {
        const { data } = user;
        if (data.birthDate) {
          const parsed = data.birthDate.split("T");
          this.dateTimeFields.date = parsed[0];
          this.dateTimeFields.time = parsed[1].split(".")[0].slice(0, -3);
        }
        this.subscription =
          data.subscription.charAt(0).toUpperCase() +
          data.subscription.slice(1);
        this.signUpDate = data.signUpDate;
        this.preloaderVisibility = false;
        this.userInfo = { ...this.userInfo, ...data };
        // Ensure language defaults to 'en' if not provided
        if (!this.userInfo.language) {
          this.userInfo.language = "en";
        }
        if (data.forcePremium.active) {
          this.endOfAccess = data.forcePremium.endDate.replace(
            /(.+)\/(.+)\/(.+)/g,
            "$3-$2-$1"
          );
          this.forceSubscription = "Force Premium";
        }
        if (this.userInfo.birthCity.cityName) {
          try {
            this.userInfo.birthCity.cityName = decodeURIComponent(
              escape(this.userInfo.birthCity.cityName)
            );
          } catch (e) {
            this.userInfo.birthCity.cityName = this.userInfo.birthCity.cityName;
          }
          this.userInfo.birthCity.cityFullName = `${this.userInfo.birthCity.cityName} (Latitude: ${this.userInfo.birthCity.lat}, Longtitude: ${this.userInfo.birthCity.lng})`;
        }
      }
    },

    fetchCountryList: debounce(async function () {
      const countryList = await SolFileAPIInstance.getCountryListWithParams(
        this.countrySearch
      );
      this.countriesList = countryList && countryList.data;
    }, 300),

    fetchCityList: debounce(async function () {
      const params = {
        countryName: this.userInfo.birthCountry,
        cityName: this.citySearch,
      };
      const cityList = await SolFileAPIInstance.getCityListWithParams(params);
      this.citiesList =
        cityList &&
        cityList.data.map((item) => {
          return {
            ...item,
            cityFullName: `${item.cityName} (Latitude: ${item.lat}, Longitude: ${item.lng})`,
          };
        });
    }, 300),

    getSelectedCountry(value) {
      this.countrySearch = value;
      if (this.countrySearch) {
        this.userInfo.birthCity = null;
        this.fetchCountryList();
      }
    },

    getSelectedCity(value) {
      this.citySearch = value;
      this.citySearch && this.fetchCityList();
    },

    async updateUserSubcription() {
      this.awaitResponse = true;
      const response =
        await SolFileAPIInstance.handleGetIndividualUserSubscription(
          this.id
        )
        .finally((res) => {
          this.awaitResponse = false;
          this.checkComplete = true;
          setTimeout(() => {
            this.checkComplete = false;
          }, 3000);
          return res;
        });
      this.userInfo.lastSubscriptionCheck = response.lastChecked
      this.subscription = capitalize(response.status);
    },

    objectSanitizer(data) {
      return Object.entries(data).reduce((target, item) => {
        const [key, value] = item;
        if (value) {
          target[key] = typeof value === "string" ? value.trim() : value;
        }
        return target;
      }, {});
    },

    async handleFormSubmit() {
      if (this.formValidation()) {
        await SolFileAPIInstance.setUserInfoById(this.id, {
          ...this.objectSanitizer(this.userInfo),
          birthCity: this.userInfo.birthCity,
          birthCountry: this.userInfo.birthCountry,
          birthDate: `${this.dateTimeFields.date}T${this.dateTimeFields.time}:00`,
          language: this.userInfo.language,
          forcePremium: {
            active: this.forceSubscription === "Force Premium",
            endDate: this.forceSubscription === "Force Premium" ? this.endOfAccess : null,
          }
      } );
      }
    },

    handlePopupClose() {
      this.modalView = false;
    },

    handlePopupOpen() {
      this.modalView = true;
    },

    async handleDeleteUser() {
      //TODO add api call
      await SolFileAPIInstance.deleteUserById(this.id);
      this.modalView = false;
      this.handleRedirect();
    },

    handleRedirect() {
      this.$router.push({
        path: `/individuals`,
      });
    },

    formValidation() {
      this.errorMessage = [];
      this.highlightingData = {
        firstName: false,
        lastName: false,
        passwordConfirm: false,
        password: false,
        cityOfBirth: false,
        countryOfBirth: false,
        email: false,
      };
      const { password, email, firstName, lastName, birthCity, birthCountry } =
        this.userInfo;

      const { date, time } = this.dateTimeFields;

      const { passwordConfirm } = this.validateFields;

      if (passwordConfirm !== password) {
        if (password.trim().length <= 6) {
          this.highlightingData.password = true;
          this.errorMessage.push("Please enter correct password");
        }
        this.errorMessage.push(
          "Password and confirm password fields should be the same"
        );
      }

      if (!email.trim()) {
        this.highlightingData.email = true;
        this.errorMessage.push("Please specify email address");
      }
      if (!firstName.trim()) {
        this.highlightingData.firstName = true;
        this.errorMessage.push("Please specify first name");
      }
      if (!lastName.trim()) {
        this.highlightingData.lastName = true;
        this.errorMessage.push("Please specify last name");
      }
      if (!date) {
        this.errorMessage.push("Please specify date of birth");
      }
      if (!time) {
        this.errorMessage.push("Please specify time of birth");
      }
      if (!birthCity) {
        this.highlightingData.cityOfBirth = true;
        this.errorMessage.push("Please specify city of birth");
      }
      if (!birthCountry) {
        this.highlightingData.countryOfBirth = true;
        this.errorMessage.push("Please specify country of birth");
      }
      setTimeout(() => {
        this.errorMessage = [];
      }, 2000);
      return !this.errorMessage.length;
    },
  },
};
</script>

<style scoped lang="scss">
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 15px;
}

input {
  -webkit-appearance: none;
}

.row {
  margin: 0 -15px;
  display: flex;
  flex-wrap: wrap;
}

.col-6 {
  @media (min-width: 1199px) {
    flex: 0 50%;
    max-width: 50%;
  }

  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  padding: 0 15px;
  flex: 0 1 100%;
  max-width: 100%;

  &.buttons-wrapper {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
    @media (max-width: 767px) {
      justify-content: center;
      margin-bottom: 10px;
    }
    & div{
      display: flex;
      flex-direction: row;
    }
  }

  &.align-end {
    justify-content: flex-end;

    @media (max-width: 767px) {
      justify-content: space-between;
    }
  }
}

.edit-page-wrapper {
  padding: 10px;

  .vue-select{
    margin-bottom: 20px;
    display: block;
  }

  input {
    box-sizing: border-box;
    display: block;
    padding: 15px;
    background-color: #fff;
    border: none;
    color: black;
    border-radius: 6px;
    width: 100%;
    margin: 0 auto 20px;

    &:focus {
      outline: none;
    }

    &.not-filled {
      border: 1px solid red;
    }
  }

  button,
  input[type="submit"] {
    box-sizing: border-box;
    padding: 15px;
    background-color: #fff;
    border: none;
    color: black;
    border-radius: 6px;
    width: 150px;
    margin: 0 10px 0 0;
    cursor: pointer;
    transition: ease all 0.4s;

    @media (max-width: 767px) {
      margin: 0;
    }

    &:focus {
      outline: none;
    }

    &:hover {
      color: white;
      background-color: #34558b;
    }

    &:last-child {
      margin: 0;
    }
  }

  .isRequired {
    .v-select.vs--single.vs--searchable,
    input {
      border: 1px solid red;
    }
  }
  input:disabled {
    background: #c4c4c4;
    color: #333;
    cursor: not-allowed;
  }
  .check-subscription-wrapper{
    display: flex;
    justify-content: space-between;
    align-items: center;
    
    p{
      margin: 0;
      line-height: 1.4;
      margin-right: 20px;
    }
  }
  .check-subscription-btn {
    align-self: flex-end;
    width: 100%;
    max-width: 220px;
    text-align: center;
    position: relative;
    &.checking {
      pointer-events: none;
    }
    .sub-message {
      position: absolute;
      top: 110%;
      left: 5px;
      color: rgb(0, 216, 0);
      font-weight: 500;
      font-size: 14px;
      opacity: 0;
      transition: 0.3s ease-in;
      &.active {
        opacity: 1;
      }
    }
  }
}
</style>